// components/admin-panel-pages/AboutPageManager.jsx - সম্পূর্ণ আপডেটেড
import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { uploadToCloudinary, isCloudinaryImage } from '../utils/cloudinary';

const AboutPageManager = ({ setMessage }) => {
    const { apiRequest } = useAdmin();
    const [aboutData, setAboutData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageSource, setImageSource] = useState('url');

    const [formData, setFormData] = useState({
        aboutText: '',
        missionStatement: '',
        visionStatement: '',
        stats: {
            toursCompleted: 1000,
            happyClients: 5000,
            destinations: 50,
            yearsExperience: 15
        }
    });

    useEffect(() => {
        loadAboutData();
    }, []);

    const loadAboutData = async () => {
        try {
            setLoading(true);
            const data = await apiRequest('/about/admin');

            if (data.success) {
                setAboutData(data.aboutPage);
                setFormData({
                    aboutText: data.aboutPage.aboutText || '',
                    missionStatement: data.aboutPage.missionStatement || '',
                    visionStatement: data.aboutPage.visionStatement || '',
                    stats: data.aboutPage.stats || {
                        toursCompleted: 1000,
                        happyClients: 5000,
                        destinations: 50,
                        yearsExperience: 15
                    }
                });

                // Set image preview and determine source
                if (data.aboutPage.aboutImage) {
                    setImagePreview(data.aboutPage.aboutImage);

                    if (isCloudinaryImage(data.aboutPage.aboutImage)) {
                        setImageSource('upload');
                    } else {
                        setImageSource('url');
                    }
                }
            }
        } catch (error) {
            console.error('Error loading about data:', error);
            setMessage({
                type: 'error',
                text: 'Error loading about page data: ' + error.message
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStatChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            stats: {
                ...prev.stats,
                [name]: parseInt(value) || 0
            }
        }));
    };

    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageUpload = async () => {
        if (!selectedImage) {
            setMessage({ type: 'error', text: 'Please select an image first' });
            return;
        }

        if (!selectedImage.type.startsWith('image/')) {
            setMessage({ type: 'error', text: 'Please select a valid image file (JPEG, PNG, WebP)' });
            return;
        }

        if (selectedImage.size > 5 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image size should be less than 5MB' });
            return;
        }

        setUploadingImage(true);

        try {
            setMessage({ type: 'info', text: 'Uploading image to Cloudinary...' });

            const result = await uploadToCloudinary(selectedImage);

            // Update form data with Cloudinary URL
            setImagePreview(result.url);
            setImageSource('upload');

            setMessage({ type: 'success', text: 'Image uploaded successfully to Cloudinary!' });
            setSelectedImage(null);
        } catch (error) {
            console.error('Cloudinary upload error:', error);
            setMessage({ type: 'error', text: 'Error uploading image: ' + error.message });
        } finally {
            setUploadingImage(false);
        }
    };

    const handleRemoveImage = () => {
        setImagePreview('assets/img/about.jpg');
        setImageSource('url');
        setSelectedImage(null);
        setMessage({ type: 'info', text: 'Image reset to default' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const submitData = {
                ...formData,
                aboutImage: imagePreview || 'assets/img/about.jpg'
            };

            const response = await apiRequest('/about/update', {
                method: 'PUT',
                body: submitData
            });

            if (response.success) {
                setMessage({ type: 'success', text: 'About page updated successfully!' });
                await loadAboutData(); // Reload data
            }
        } catch (error) {
            console.error('Error updating about page:', error);
            setMessage({ type: 'error', text: 'Error updating about page: ' + error.message });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading about page data...</p>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <div className="row mb-4">
                <div className="col-12">
                    <div className="card shadow-sm">
                        <div className="card-header bg-primary text-white">
                            <h4 className="mb-0">
                                <i className="fas fa-info-circle me-2"></i>
                                About Page Management
                            </h4>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-md-8">
                    <div className="card">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fas fa-image me-2"></i>
                                About Page Image
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="text-center mb-4">
                                {imagePreview && (
                                    <div className="position-relative mb-4">
                                        <img
                                            src={imagePreview}
                                            alt="About Page Preview"
                                            className="img-fluid rounded mb-3"
                                            style={{
                                                maxHeight: '300px',
                                                objectFit: 'cover',
                                                width: '100%'
                                            }}
                                            onError={(e) => {
                                                e.target.src = 'assets/img/about.jpg';
                                            }}
                                        />
                                        {!imagePreview.includes('assets/img/about.jpg') && (
                                            <button
                                                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                                                onClick={handleRemoveImage}
                                                disabled={uploadingImage || saving}
                                                title="Reset to Default Image"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        )}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Image Source</label>
                                    <div className="mb-3">
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="imageSource"
                                                id="imageSourceUrl"
                                                value="url"
                                                checked={imageSource === 'url'}
                                                onChange={() => setImageSource('url')}
                                                disabled={uploadingImage || saving}
                                            />
                                            <label className="form-check-label" htmlFor="imageSourceUrl">
                                                Use Image URL
                                            </label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="imageSource"
                                                id="imageSourceUpload"
                                                value="upload"
                                                checked={imageSource === 'upload'}
                                                onChange={() => setImageSource('upload')}
                                                disabled={uploadingImage || saving}
                                            />
                                            <label className="form-check-label" htmlFor="imageSourceUpload">
                                                Upload Image
                                            </label>
                                        </div>
                                    </div>

                                    {imageSource === 'url' && (
                                        <div className="mb-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={imagePreview || ''}
                                                onChange={(e) => setImagePreview(e.target.value)}
                                                placeholder="/assets/img/about.jpg or https://example.com/image.jpg"
                                                disabled={uploadingImage || saving}
                                            />
                                            <div className="form-text">
                                                Enter image URL or path. Default: /assets/img/about.jpg
                                            </div>
                                        </div>
                                    )}

                                    {imageSource === 'upload' && (
                                        <div className="mb-3">
                                            <div className="input-group">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={handleImageSelect}
                                                    className="form-control"
                                                    disabled={uploadingImage || saving}
                                                />
                                                <button
                                                    className="btn btn-primary"
                                                    onClick={handleImageUpload}
                                                    disabled={!selectedImage || uploadingImage}
                                                >
                                                    {uploadingImage ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                            Uploading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-upload me-2"></i>
                                                            Upload to Cloudinary
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                            <div className="form-text">
                                                Supported formats: JPG, PNG, WebP. Max size: 5MB
                                            </div>

                                            {selectedImage && !uploadingImage && (
                                                <div className="alert alert-info mt-2">
                                                    <i className="fas fa-info-circle me-2"></i>
                                                    Selected: {selectedImage.name} ({(selectedImage.size / 1024 / 1024).toFixed(2)} MB)
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {uploadingImage && (
                                        <div className="alert alert-info">
                                            <div className="d-flex align-items-center">
                                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <span>Uploading image to Cloudinary...</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card mt-4">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fas fa-chart-bar me-2"></i>
                                Statistics
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Tours Completed</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="toursCompleted"
                                        value={formData.stats.toursCompleted}
                                        onChange={handleStatChange}
                                        min="0"
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Happy Clients</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="happyClients"
                                        value={formData.stats.happyClients}
                                        onChange={handleStatChange}
                                        min="0"
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Destinations</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="destinations"
                                        value={formData.stats.destinations}
                                        onChange={handleStatChange}
                                        min="0"
                                        disabled={saving}
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Years Experience</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        name="yearsExperience"
                                        value={formData.stats.yearsExperience}
                                        onChange={handleStatChange}
                                        min="0"
                                        disabled={saving}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-md-4">
                    <div className="card">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fas fa-edit me-2"></i>
                                About Page Content
                            </h6>
                        </div>
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Main About Text</label>
                                    <textarea
                                        className="form-control"
                                        name="aboutText"
                                        rows="4"
                                        value={formData.aboutText}
                                        onChange={handleInputChange}
                                        placeholder="Enter main about text..."
                                        disabled={saving}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Mission Statement</label>
                                    <textarea
                                        className="form-control"
                                        name="missionStatement"
                                        rows="3"
                                        value={formData.missionStatement}
                                        onChange={handleInputChange}
                                        placeholder="Enter mission statement..."
                                        disabled={saving}
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="form-label">Vision Statement</label>
                                    <textarea
                                        className="form-control"
                                        name="visionStatement"
                                        rows="3"
                                        value={formData.visionStatement}
                                        onChange={handleInputChange}
                                        placeholder="Enter vision statement..."
                                        disabled={saving}
                                    />
                                </div>

                                <div className="d-grid gap-2">
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || uploadingImage}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-save me-2"></i>
                                                Save Changes
                                            </>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={loadAboutData}
                                        disabled={saving || uploadingImage}
                                    >
                                        <i className="fas fa-sync me-2"></i>
                                        Reload Data
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="card mt-4">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fas fa-image me-2"></i>
                                Current Status
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="mb-3">
                                <strong>Image Source:</strong>
                                <div className="mt-1">
                                    <span className={`badge ${imageSource === 'upload' ? 'bg-success' : 'bg-info'}`}>
                                        {imageSource === 'upload' ? 'Cloudinary Upload' : 'URL'}
                                    </span>
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>Image URL:</strong>
                                <div className="mt-1 small text-truncate" title={imagePreview}>
                                    {imagePreview || 'assets/img/about.jpg'}
                                </div>
                            </div>
                            <div className="mb-3">
                                <strong>Statistics Total:</strong>
                                <div className="mt-1">
                                    {Object.values(formData.stats).reduce((a, b) => a + b, 0).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Section */}
            <div className="row mt-4">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header bg-light">
                            <h6 className="mb-0">
                                <i className="fas fa-eye me-2"></i>
                                Live Preview
                            </h6>
                        </div>
                        <div className="card-body">
                            <div className="row g-5">
                                <div className="col-lg-6" style={{ minHeight: '400px' }}>
                                    <div className="position-relative h-100 border rounded overflow-hidden">
                                        {imagePreview ? (
                                            <img
                                                src={imagePreview}
                                                alt="About Preview"
                                                className="w-100 h-100"
                                                style={{ objectFit: 'cover' }}
                                                onError={(e) => {
                                                    e.target.src = 'assets/img/about.jpg';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-light">
                                                <i className="fas fa-image fa-3x text-muted"></i>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-lg-6">
                                    <h6 className="section-title text-primary">About Us</h6>
                                    <h3 className="mb-4">
                                        Welcome to <span className="text-primary">Asian Travel Agency</span>
                                    </h3>
                                    <p className="mb-3">{formData.aboutText || 'No content yet'}</p>
                                    <p className="mb-3">{formData.missionStatement || 'No mission statement yet'}</p>
                                    <p className="mb-4">{formData.visionStatement || 'No vision statement yet'}</p>

                                    <div className="row gy-2 gx-4 mb-4">
                                        <div className="col-sm-6">
                                            <p className="mb-2">
                                                <i className="fa fa-arrow-right text-primary me-2"></i>
                                                First Class Flights
                                            </p>
                                        </div>
                                        <div className="col-sm-6">
                                            <p className="mb-2">
                                                <i className="fa fa-arrow-right text-primary me-2"></i>
                                                Handpicked Hotels
                                            </p>
                                        </div>
                                    </div>

                                    <a className="btn btn-primary py-2 px-4 mt-2" href="#">
                                        Contact Us
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AboutPageManager;